import { useState } from 'react'
import { Box, Divider, ListItemIcon, Menu, MenuItem, TextField } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import type { RespondentTag } from '@/services/respondentBaseService'
import { hueFor } from './visuals'

type Props = {
  anchorEl: HTMLElement | null
  onClose: () => void
  groups: RespondentTag[]
  /** `null` = "no group"; omit the option by passing `allowNone={false}`. */
  onPick: (groupName: string | null) => void
  allowNone?: boolean
}

/** Pick an existing group or type a new one — used by the composer and the selection bar. */
export default function GroupMenu({ anchorEl, onClose, groups, onPick, allowNone = true }: Props) {
  const { t } = useTranslation('respondents')
  const c = useTheme().affexy.colors
  const [draft, setDraft] = useState('')

  const pick = (name: string | null) => {
    onPick(name)
    setDraft('')
    onClose()
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      slotProps={{ paper: { sx: { minWidth: 240, borderRadius: '14px', mt: 0.5 } } }}
    >
      {allowNone && (
        <MenuItem onClick={() => pick(null)} sx={{ fontSize: 14, color: c.ink56 }}>
          <ListItemIcon sx={{ minWidth: '20px !important' }}>
            <Dot color={c.ink32} />
          </ListItemIcon>
          {t('group.none')}
        </MenuItem>
      )}
      {groups.map((group) => (
        <MenuItem key={group.id} onClick={() => pick(group.name)} sx={{ fontSize: 14 }}>
          <ListItemIcon sx={{ minWidth: '20px !important' }}>
            <Dot color={hueFor(group.id)} />
          </ListItemIcon>
          {group.name}
          <Box component="span" sx={{ ml: 'auto', pl: 2, color: c.ink32, fontSize: 13 }}>
            {group.respondent_count}
          </Box>
        </MenuItem>
      ))}
      {(groups.length > 0 || allowNone) && <Divider />}
      <Box sx={{ px: 1.5, py: 1 }} onKeyDown={(e) => e.stopPropagation()}>
        <TextField
          size="small"
          fullWidth
          label={t('group.newLabel')}
          placeholder={t('group.newPlaceholder')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && draft.trim()) {
              e.preventDefault()
              pick(draft.trim())
            }
          }}
        />
      </Box>
    </Menu>
  )
}

export function Dot({ color, size = 8 }: { color: string; size?: number }) {
  return <Box component="span" sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flex: 'none', display: 'inline-block' }} />
}
